// external
import axios from 'axios';

// libraries
import { getCurrentAxiosRequestInterceptor } from '@makemydeal/dr-shared-network';
import { apiConfigHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import type { ScopedStore } from '@makemydeal/shared-scoped-store';

// selectors
import { getGeneratePdfPayload } from '../selectors/printPdfDealSelectors';

// actions
import { printPdfLoading } from '../actions/printPdfActionCreator';
import { saveOffer } from '../actions/offerActionCreators';

// services
import { ensurePrintingLogo } from './ensurePrintingLogo';

// interfaces/types
import type { DashStore } from '@makemydeal/dr-shared-store';

const ENDPOINT_PATH = '/generate-pdf';

/**
 * Service to call the customizable PDF generation endpoint
 * @param store - Redux store
 * @param forSharing - Whether the PDF is for sharing ('text', 'email') or direct viewing (false)
 * @param selectedProposalId - Optional proposal ID for customizable PDFs
 */
export const callGeneratePdfService = async (store: DashStore, type: string) => {
    // MUST run before the payload is built: `dealerLogo` is read from state, not passed in.
    await ensurePrintingLogo(store);

    const realState = (store as ScopedStore).getRealState();
    const pdfPayload = getGeneratePdfPayload(realState, type);
    const locHref = locHrefUtil.getLocHref();

    // Add query parameter for short URL generation when sharing
    const endpoint = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, ENDPOINT_PATH).url;

    const axiosRequestInterceptor = getCurrentAxiosRequestInterceptor();
    const { headers } = await axiosRequestInterceptor({ url: endpoint });

    const startTime = performance.now();
    const nrEventName = 'MV:PrintGeneratePdf';

    try {
        store.dispatch(saveOffer({ isDraft: true }));
        store.dispatch(printPdfLoading(true));

        const response = await axios.post(endpoint, pdfPayload, {
            headers: {
                Authorization: headers.Authorization
            }
        });

        const pdfUrl = response.data.data.url;

        logNewRelicPageAction(nrEventName, {
            durationMs: performance.now() - startTime,
            result: 'success',
            endpoint: ENDPOINT_PATH
        });

        window.open(pdfUrl, '_blank')?.focus();
    } catch (err) {
        logNewRelicPageAction(nrEventName, {
            durationMs: performance.now() - startTime,
            result: 'error',
            endpoint: ENDPOINT_PATH
        });
    } finally {
        store.dispatch(printPdfLoading(false, false));
    }
};
